import { INPUT_TYPES } from 'app-constants/inputTypes';
import { ACTION_TYPES, COMMENT } from 'app-constants/actionTypes';
import { omit } from 'ramda';
import { isNotEmpty } from 'utils/formUtils';
import { BOT_UPDATE } from 'utils/toastDispacher';
import { getBotspecInfo } from 'data/selectors';
import { updateParsingBotspec, postBotspec } from '../chatbots';
import calculateActionOutput from '../outputTypes';
import { injecVariablesInputType } from '../inputTypes';
import {
  validateSpec, updateBotspecParsingErrors, updateIntegrationUrl, findDuplicateNodeTitles,
} from '../specValidator';
import { processRulesTransitions, createRulesActions, createRulesFunction } from './rulesParser';
import slugify from '../utils';

// Núcleo común de los parsers rete_json → spec_json. Lo que cambia entre versiones
// del spec (1.0 lo ejecuta twnel-cf-api-botcore, 2.0 twnel-cf-api-botcore-edge) lo
// declara cada versión en un perfil; ver createSpecParser.

export const VARIABLE_PATTERN = /\{\u00A0((?:\w?)?(?:_)?\w+)\u00A0\}/g;
const USER_VARIABLE_PATTERN = 'user_';
const CHATBOT_VARIABLE_PATTERN = '_';
const GLOBAL_VARIABLE_PATTERN = 'global_';

// Id de la transición a la que lleva la primera conexión de una salida.
export const nextTransitionIdFor = (titleKey) => (nodes, connections) => {
  const nextTransitionKey = connections.length > 0 ? connections[0].node : '';
  return nextTransitionKey ? slugify(`${titleKey(nodes[nextTransitionKey].data.title)}${nextTransitionKey}`) : '';
};

const buildTransitionKey = (type, outputKey) => {
  if (ACTION_TYPES.CALL_FUNCTION === type || ACTION_TYPES.CONDITIONAL_STEP === type) {
    return outputKey;
  }

  return slugify(`${outputKey}`);
};

const verifyMetadata = (metadata) => isNotEmpty(metadata)
  && Object.values(metadata).every((val) => !!val);

const functionPath = (functionName, globalFunctionNames) => (
  globalFunctionNames.includes(functionName) ? `global.${functionName}` : `session.${functionName}`
);

const parseMapResultAction = (properties) => ({
  type: 'map_result',
  vars: properties.vars,
  eval: {
    targets: Object.keys(properties.targets).reduce(
      (accum, key) => [
        ...accum,
        {
          from: `${properties.targets[key].from}`, assign: properties.targets[key].assignments,
        },
      ], [],
    ),
  },
});

const parseEvalNextAction = (properties, globalFunctionNames = []) => ({
  type: 'eval_next',
  vars: properties.vars,
  eval: {
    function: functionPath(properties.eval.function, globalFunctionNames),
    input: properties.eval.input,
  },
});

const parseConditionalStepAction = (properties, globalFunctionNames = []) => ({
  type: 'conditional_step',
  vars: properties.vars,
  eval: {
    function: functionPath(properties.eval.function, globalFunctionNames),
    input: properties.eval.input,
  },
});

const parseStoreDataAction = (properties) => ({
  type: 'store_data',
  eval: properties.eval,
});

const parseStoreFileAction = (properties) => ({
  type: 'store_data',
  eval: properties.eval,
});

// Reemplaza una variable del editor ({ nombre }) por su ruta Jinja en el spec.
const replacer = (variables, isJinja = true) => (match) => {
  const key = match.split(VARIABLE_PATTERN).filter((key) => !!key)[0];
  if (key.startsWith(USER_VARIABLE_PATTERN)) {
    const formattedKey = key.replace('user_', '');
    return `{{ variables.user.${formattedKey} }}`;
  }
  if (key.startsWith(GLOBAL_VARIABLE_PATTERN)) {
    const formattedKey = key.replace('global_', '');
    return `{{ variables.global.${formattedKey} }}`;
  }
  if (key.startsWith(CHATBOT_VARIABLE_PATTERN)) {
    const formattedKey = key.substring(1);
    return (variables[formattedKey] && `{{ variables.chatbot.${formattedKey} }}`) || key;
  }

  if (!isJinja) {
    return `variables.session.${key}`;
  }

  return `{{ variables.session.${key} }}`;
};

const injectKeyValue = (properties, variables) => Object.entries(properties).reduce(
  (accum, [key, val]) => ({
    ...accum,
    [key.replace(VARIABLE_PATTERN, replacer(variables)) || key]: val.replace(
      VARIABLE_PATTERN, replacer(variables),
    ),
  }), {},
);

const injectVariablesOnTargets = (targets, variables) => Object.keys(targets).reduce(
  (accum, targetKey) => {
    const target = targets[targetKey];
    return {
      ...accum,
      [targetKey]: {
        from: target.from.replace(VARIABLE_PATTERN, replacer(variables)),
        assignments: target.assignments.reduce((accum, assignment) => [
          ...accum,
          {
            map: assignment.map.replace(VARIABLE_PATTERN, replacer(variables)),
            to: assignment.to.replace(VARIABLE_PATTERN, replacer(variables, false)),
          },
        ], []),
      },
    };
  }, {},
);

const injectVariablesOnSendMessage = (nodeProperties, variables) => {
  const {
    messages, input, mediaUrl,
    metadata, targets,
  } = nodeProperties;
  const injectMessages = (messages) => Object.keys(messages).reduce((accum, key) => ({
    ...accum,
    [key]: {
      ...messages[key],
      text: messages[key].text.replace(VARIABLE_PATTERN, replacer(variables)),
      data: Object.keys(messages[key].data).reduce(
        (accum, dataKey) => (
          { ...accum, [dataKey]: injectKeyValue(messages[key].data[dataKey], variables) }
        ), {},
      ),
    },
  }), {});

  return {
    ...nodeProperties,
    messages: messages ? injectMessages(messages) : {},
    input: isNotEmpty(input)
      ? injecVariablesInputType(input, VARIABLE_PATTERN, replacer(variables)) : {},
    mediaUrl: mediaUrl?.replace(VARIABLE_PATTERN, replacer(variables)) ?? '',
    metadata: metadata ? injectKeyValue(metadata, variables) : {},
    targets: targets ? injectVariablesOnTargets(targets, variables) : {},
  };
};

const injectVariablesOnCallApi = (nodeProperties, variables) => {
  const {
    url, params, headers, body,
    targets,
  } = nodeProperties;

  const injectOnEntries = (entries) => Object.keys(entries).reduce((accum, key) => ({
    ...accum,
    [key]: injectKeyValue(entries[key], variables),
  }), {});

  return {
    ...nodeProperties,
    url: url.replace(VARIABLE_PATTERN, replacer(variables)),
    params: injectOnEntries(params),
    headers: injectOnEntries(headers),
    body: body.replace(VARIABLE_PATTERN, replacer(variables)),
    targets: targets ? injectVariablesOnTargets(targets, variables) : {},
  };
};

const injectVariablesGeneric = (nodeProperties, variables) => {
  const { eval: functionEval, targets } = nodeProperties;

  return {
    ...nodeProperties,
    eval: {
      ...functionEval,
      input: functionEval.input ? injectKeyValue(functionEval.input, variables) : {},
    },
    targets: targets ? injectVariablesOnTargets(targets, variables) : {},
  };
};

const injectVariablesOnMapResult = (nodeProperties, variables) => {
  const { targets } = nodeProperties;

  return {
    ...nodeProperties,
    targets: targets ? injectVariablesOnTargets(targets, variables) : {},
  };
};

const unchanged = (nodeProperties) => nodeProperties;

const injectVariablesOnAction = (type, nodeProperties, variables) => {
  const injectors = {
    [ACTION_TYPES.SEND_MESSAGE]: injectVariablesOnSendMessage,
    [ACTION_TYPES.CALL_API]: injectVariablesOnCallApi,
    [ACTION_TYPES.MAP_RESULT]: injectVariablesOnMapResult,
    [ACTION_TYPES.CALL_FUNCTION]: injectVariablesGeneric,
    [ACTION_TYPES.EVAL_NEXT]: injectVariablesGeneric,
    [ACTION_TYPES.STORE_DATA]: unchanged,
    [ACTION_TYPES.STORE_FILE]: unchanged,
    [ACTION_TYPES.CONDITIONAL_STEP]: injectVariablesGeneric,
  };

  return injectors[slugify(type)](nodeProperties, variables);
};

const createImplicitFunction = (functionId, radioOptions) => ({
  [functionId]: {
    runtime: 'js',
    version: '5',
    code: `function ${functionId}(input) {\n  const operation = input.operation;\n  return operation;\n}`,
    input: {
      operation: 'string',
    },
    output: {
      type: 'string',
      values: radioOptions,
    },
  },
});

export const filterVariablesMetadata = (variables) => Object.keys(variables).reduce(
  (accum, key) => {
    const { isMapped, ...properties } = variables[key];
    return { ...accum, [key]: properties };
  }, {},
);

// Radio con variables de resultado (y opciones fijas): lleva un map_result y un
// conditional_step implícitos.
const isImplicitRadio = ({ type, input, resultVariables }) => isNotEmpty(resultVariables)
  && type === ACTION_TYPES.SEND_MESSAGE && input?.type === INPUT_TYPES.RADIO
  && !input?.optionsVariable;

/**
 * Parser de una versión del spec. `profile` declara lo que cambia entre versiones:
 * - titleKey(title): base de las claves de transición y acción a partir del título del nodo.
 * - rulesTitle: formato del título para las reglas del conditional_step (rulesParser);
 *   rulesActionTitle(title): título que reciben sus acciones.
 * - parseInputType(input) y parseCallApiAction(properties, nodeId, nodes): formato de
 *   las entradas del send_message y del call_api.
 * - callFunctionInput(input, variables): entradas de un call_function.
 * - mapResultFrom(path): `from` de los map_result implícitos.
 * - radioOptionValue(option): valor de una opción de radio en las salidas implícitas.
 * - implicitConditionalStep: { transitionId(title, nodeId), actionKey(nodeId) }.
 * - uniqueImplicitTransitions: agrega el id del nodo a las claves de las transiciones
 *   implícitas (`map_result_implicit_<título><id>`). Es obligatorio si `titleKey` descarta
 *   parte del título (spec 1.0 quita el `#N`): sin el id, todos los nodos "Send Message"
 *   comparten la misma transición implícita y sólo sobrevive la del último.
 * - finalize(spec, { actions, specJson }): ajustes finales del spec (opcional).
 */
export const createSpecParser = (profile) => {
  const { titleKey } = profile;
  const nextTransitionId = nextTransitionIdFor(titleKey);

  const buildImplicitMapResultTransition = (nodeId, actualNode, nodes) => {
    const { data, outputs } = actualNode;
    const { properties } = data;
    const title = titleKey(data.title);
    const implicitKey = (prefix) => slugify(
      `${prefix}_${title}${profile.uniqueImplicitTransitions ? nodeId : ''}`,
    );

    if (!isNotEmpty(properties.resultVariables)) return {};
    const withImplicitCondition = isImplicitRadio(properties)
      || properties.type === ACTION_TYPES.CONDITIONAL_STEP;

    if (withImplicitCondition) {
      return {
        [slugify(`${title}${nodeId}`)]: {
          next: implicitKey('map_result_implicit'),
          action: slugify(`${title}${nodeId}_action`),
        },
        [implicitKey('map_result_implicit')]: {
          next: implicitKey('conditional_step_implicit'),
          action: slugify(`map_result_implicit${nodeId}_action`),
        },
        [implicitKey('conditional_step_implicit')]: {
          next: Object.keys(outputs).reduce((nodeAccum, outputKey) => {
            const { connections } = outputs[outputKey];
            return { ...nodeAccum, [outputKey]: nextTransitionId(nodes, connections) };
          }, {}),
          action: slugify(`conditional_step_implicit_${nodeId}_action`),
        },
      };
    }
    if (properties.type !== ACTION_TYPES.MAP_RESULT) {
      const { connections } = outputs[Object.keys(outputs)[0]];
      return {
        [slugify(`${title}${nodeId}`)]: {
          next: implicitKey('map_result_implicit'),
          action: slugify(`${title}${nodeId}_action`),
        },
        [implicitKey('map_result_implicit')]: {
          next: nextTransitionId(nodes, connections),
          action: slugify(`map_result_implicit${nodeId}_action`),
        },
      };
    }
    return {};
  };

  const parseNodeTransitions = (nodes) => Object.keys(nodes).reduce((accum, nodeId) => {
    const node = nodes[nodeId];
    const { title, properties } = node.data;
    const key = titleKey(title);

    const implicitMapResultTransition = buildImplicitMapResultTransition(nodeId, node, nodes);
    const conditionsTransitions = processRulesTransitions(nodeId, node, nodes, profile.rulesTitle);

    /** @type {string | Record<string, string>} Una salida: id; varias: id por salida. */
    let next = '';

    const filteredOutputs = Object.keys(node.outputs).filter((outputKey) => outputKey !== 'on_error');
    if (filteredOutputs.length > 1) {
      next = filteredOutputs.reduce((nodeAccum, outputKey) => {
        const { connections } = node.outputs[outputKey];
        const outputId = buildTransitionKey(properties.type, outputKey);
        return { ...nodeAccum, [outputId]: nextTransitionId(nodes, connections) };
      }, {});
    } else if (filteredOutputs.length === 1) {
      const { connections } = node.outputs[filteredOutputs[0]];
      next = nextTransitionId(nodes, connections);
    }

    return {
      ...accum,
      [slugify(`${key}${nodeId}`)]: {
        next,
        action: slugify(`${key}${nodeId}_action`),
      },
      ...implicitMapResultTransition,
      ...conditionsTransitions,
    };
  }, {});

  const parseSendMessageAction = (properties) => {
    const parseMessages = (messages) => Object.keys(messages).reduce((accum, key) => {
      const message = messages[key];

      if (message.data && Object.keys(message.data).length) {
        const data = Object.keys(message.data).reduce((dataAccum, dataKey) => {
          const { name, value } = message.data[dataKey];
          return { ...dataAccum, [name]: value };
        }, {});
        return [...accum, { template: message.text, data }];
      }

      return [...accum, message.text];
    }, []);

    return {
      type: 'send_message',
      ...isNotEmpty(properties?.vars) && { vars: properties.vars },
      ...properties.messages && { messages: parseMessages(properties.messages) },
      ...isNotEmpty(properties?.input) && { input: profile.parseInputType(properties.input) },
      ...properties?.closeChat && { close_chat: !properties.closeChat },
      ...properties?.mediaUrl && { media_url: properties.mediaUrl },
      ...properties?.delay > 0 && { delay: properties.delay },
      ...properties?.timeout > 0 && properties?.timeout <= 10 && { timeout: properties.timeout },
      ...isNotEmpty(properties?.metadata) && verifyMetadata(properties?.metadata)
      && { metadata: properties.metadata },
    };
  };

  const parseCallFunctionAction = (properties, nodeId, nodes, variables, globalFunctionNames) => {
    const { connections } = nodes[nodeId].outputs.on_error;
    const onErrorNextTransition = connections.length > 0 ? nextTransitionId(nodes, connections) : '';

    return {
      type: 'call_function',
      vars: properties.vars,
      eval: {
        function: functionPath(properties.eval.function, globalFunctionNames),
        input: profile.callFunctionInput(properties.eval.input, variables),
      },
      ...onErrorNextTransition.length > 0 && { on_error: { next: onErrorNextTransition } },
    };
  };

  const parseImplicitMapResultAction = (nodeId, type, properties) => {
    const { input, title } = properties;
    // El runtime guarda la salida de cada paso bajo la clave de su transición, que se arma con
    // titleKey(título) (spec 1.0: sin el `#N`). Las referencias que guarda la UI usan el título
    // completo (`send_message3477234772`), que no existe como transición: se reescriben a la clave real.
    const transitionId = slugify(`${titleKey(title)}${nodeId}`);
    const storedTransitionId = slugify(`${title}${nodeId}`);
    const toTransitionKey = (reference) => (
      reference.startsWith(`transition.${storedTransitionId}.`)
        ? `transition.${transitionId}${reference.slice(`transition.${storedTransitionId}`.length)}`
        : reference
    );
    const reference = calculateActionOutput(type, transitionId, input, properties);
    const nestedReferences = Object.keys(properties.resultVariables)
      .reduce((accum, key) => ({
        ...accum,
        [key]: { reference: toTransitionKey(properties.resultVariables[key].reference) },
      }), {});

    const flattenedReferences = {
      ...reference,
      ...reference.single,
      ...nestedReferences,
    };

    return {
      type: 'map_result',
      eval: {
        targets: Object.keys(properties.resultVariables).reduce((accum, key) => {
          const fullReference = flattenedReferences[key].reference;
          const referenceParts = fullReference.split('.');
          const resultMap = referenceParts[referenceParts.length - 1].trim();

          return [...accum, {
            // lastIndexOf: el nombre del map puede aparecer antes en la ruta
            // (map "resumen" en transition.resumen_orden115.output.answer.resumen).
            from: profile.mapResultFrom(fullReference.substring(0, fullReference.lastIndexOf(`.${resultMap}`))),
            assign: [{
              map: resultMap,
              to: `variables.session.${properties.resultVariables[key].variableId}`,
            }],
          }];
        }, []),
      },
    };
  };

  const resolveImplicitMapResult = (type, properties, nodeId, variables) => {
    if (type !== ACTION_TYPES.MAP_RESULT && isNotEmpty(properties.resultVariables)) {
      const updatedProperties = injectVariablesOnMapResult(properties, variables);
      return {
        [`map_result_implicit${nodeId}_action`]: {
          ...parseImplicitMapResultAction(nodeId, type, updatedProperties),
        },
      };
    }
    return {};
  };

  const resolveImplicitConditionalStep = (properties, nodeId, title) => {
    if (!isImplicitRadio(properties)) return {};
    const { transitionId, actionKey } = profile.implicitConditionalStep;
    return {
      [actionKey(nodeId)]: {
        type: 'conditional_step',
        eval: {
          // Las funciones implícitas se guardan en session.
          function: `session.implicit_function${nodeId}`,
          input: {
            operation: `{{ ${transitionId(title, nodeId)} }}`,
          },
          outputs: Object.values(properties.input.data).map(profile.radioOptionValue),
        },
      },
    };
  };

  const parseNodeActions = (nodes, variables, globalFunctionNames = []) => {
    const parseActions = {
      [ACTION_TYPES.SEND_MESSAGE]: parseSendMessageAction,
      [ACTION_TYPES.CALL_API]: profile.parseCallApiAction,
      [ACTION_TYPES.MAP_RESULT]: parseMapResultAction,
      [ACTION_TYPES.CALL_FUNCTION]: (props, nodeId, allNodes) => parseCallFunctionAction(
        props, nodeId, allNodes, variables, globalFunctionNames,
      ),
      [ACTION_TYPES.EVAL_NEXT]: (props) => parseEvalNextAction(props, globalFunctionNames),
      [ACTION_TYPES.STORE_DATA]: parseStoreDataAction,
      [ACTION_TYPES.CONDITIONAL_STEP]:
        (props) => parseConditionalStepAction(props, globalFunctionNames),
      [ACTION_TYPES.STORE_FILE]: parseStoreFileAction,
    };

    return Object.keys(nodes).reduce((accum, nodeId) => {
      const { properties: nodeProperties, title } = nodes[nodeId].data;
      const { type } = nodeProperties;
      const propertiesWithVariables = injectVariablesOnAction(type, nodeProperties, variables);

      return {
        ...accum,
        [slugify(`${titleKey(title)}${nodeId}_action`)]: {
          ...parseActions[slugify(type)](propertiesWithVariables, nodeId, nodes),
        },
        ...resolveImplicitMapResult(type, nodeProperties, nodeId, variables),
        ...resolveImplicitConditionalStep(nodeProperties, nodeId, title),
        ...createRulesActions(type, nodeProperties, nodeId, profile.rulesActionTitle(title), variables),
      };
    }, {});
  };

  const markEntryPoint = (nodes) => {
    const key = Object.keys(nodes).find((nodeKey) => nodes[nodeKey].data.isEntryPoint);
    return nodes[key] ? `${slugify(titleKey(nodes[key].data.title))}${key}` : '';
  };

  const resolveImplicitFunctions = (nodes) => Object.keys(nodes).reduce((accum, nodeId) => {
    const { properties } = nodes[nodeId].data;
    if (isImplicitRadio(properties)) {
      const values = Object.values(properties.input.data).map(profile.radioOptionValue);
      return { ...accum, ...createImplicitFunction(`implicit_function${nodeId}`, values) };
    }
    return accum;
  }, {});

  const parseNodes = (specJson, reteJson, globalVariables = {}, globalFunctions = {}) => {
    const environment = specJson.env || 'prod';

    const variables = {
      ...specJson.variables.chatbot,
      ...filterVariablesMetadata(specJson.variables.session),
      ...specJson.variables.user,
      ...globalVariables,
    };

    // Las globales llevan un valor por ambiente; el spec solo el del ambiente actual.
    const globalVariablesProperties = Object.keys(globalVariables).reduce((acc, varKey) => {
      const varValue = globalVariables[varKey];
      const valueByEnv = environment === 'test'
        ? (varValue.valueTest ?? varValue.value_test ?? varValue.value)
        : (varValue.valueProd ?? varValue.value_prod ?? varValue.value);

      return {
        ...acc,
        [varKey]: {
          type: varValue.type,
          value: valueByEnv,
        },
      };
    }, {});

    const nodes = Object.keys(reteJson.nodes).reduce((accum, key) => {
      const actualNode = reteJson.nodes[key];
      if (slugify(actualNode.name) !== COMMENT) {
        return { ...accum, [key]: actualNode };
      }
      return accum;
    }, {});

    const transitions = parseNodeTransitions(nodes);
    const globalFunctionNames = Object.keys(globalFunctions);
    const actions = parseNodeActions(nodes, variables, globalFunctionNames);
    const entrypoint = markEntryPoint(nodes);

    // specJson.functions puede venir con la estructura nueva ({ session, global }) o la
    // antigua (las funciones de sesión directamente). Las globales no se duplican.
    const hasNewStructure = specJson.functions && (
      Object.prototype.hasOwnProperty.call(specJson.functions, 'session')
      || Object.prototype.hasOwnProperty.call(specJson.functions, 'global')
    );
    const previousSessionFunctions = hasNewStructure
      ? (specJson.functions.session || {})
      : (specJson.functions || {});
    const existingSessionFunctions = Object.keys(previousSessionFunctions)
      .filter((key) => !globalFunctionNames.includes(key))
      .reduce((acc, key) => ({ ...acc, [key]: previousSessionFunctions[key] }), {});

    const spec = {
      ...specJson,
      entrypoint,
      transitions,
      actions,
      functions: {
        session: {
          ...existingSessionFunctions,
          ...resolveImplicitFunctions(nodes),
          ...createRulesFunction(nodes, variables, profile.rulesTitle),
        },
        global: globalFunctions,
      },
      variables: {
        ...specJson.variables,
        session: filterVariablesMetadata(specJson.variables.session),
        global: filterVariablesMetadata(globalVariablesProperties),
      },
    };
    return profile.finalize ? profile.finalize(spec, { actions, specJson }) : spec;
  };

  return {
    parseNodeTransitions, parseNodeActions, markEntryPoint, parseNodes,
  };
};

const saveParsedSpec = (dispatch, botspec, specJson, reteJson) => dispatch(postBotspec({
  ...botspec,
  spec_json: specJson,
  rete_json: reteJson,
}, {
  actionId: BOT_UPDATE,
  elementName: botspec.name,
})).then(() => {
  dispatch(updateParsingBotspec(false));
});

// Thunk que genera el spec desde el rete_json del editor y guarda el botspec. Las
// variables y funciones globales se cargan con los fetchers de cada versión; si fallan,
// el spec se genera sin ellas.
export const createParseProdJson = (parseNodes, [fetchVariables, fetchFunctions]) => (json) => (
  dispatch, getState,
) => {
  dispatch(updateBotspecParsingErrors([]));
  const { botspec } = getBotspecInfo(getState());
  const reteJson = { ...botspec.rete_json, ...json };

  const errorMessages = validateSpec(reteJson);
  if (errorMessages.length !== 0) {
    dispatch(updateBotspecParsingErrors(errorMessages));
  }
  // Nombres repetidos cruzan las transiciones implícitas: guardar dejaría el bot roto.
  if (findDuplicateNodeTitles(reteJson.nodes).length > 0) return;

  const specJson = omit(['entrypoint', 'transitions', 'actions'], botspec.spec_json);

  dispatch(updateParsingBotspec(true));

  Promise.all([dispatch(fetchVariables()), dispatch(fetchFunctions())])
    .then(([globalVariables, globalFunctions]) => {
      const parsed = parseNodes(specJson, reteJson, globalVariables || {}, globalFunctions || {});
      saveParsedSpec(dispatch, botspec, updateIntegrationUrl(parsed), reteJson);
    })
    .catch(() => {
      const parsed = parseNodes(specJson, reteJson, {}, {});
      saveParsedSpec(dispatch, botspec, updateIntegrationUrl(parsed), reteJson);
    });
};
